import type { UUIDString } from '@aigc/types';
import type { UploadFile } from 'antd';
import { array, mixed, type TestFunction } from 'yup';
import { FileValidators } from '../file';
import type { UploadFileOutputDto } from '../file/model';
import type { SkillAttachmentDto } from './model';

export const SKILL_ATTACHMENT_ACCEPT = [
  '.pdf',
  'image/*',
  'text/*',
  '.json',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
];
export const MAX_SKILL_ATTACHMENT_COUNT = 10;
export const MAX_SKILL_ATTACHMENT_SIZE = 10_000_000;

export const skillAttachmentValidator = FileValidators.compose([
  FileValidators.accept(SKILL_ATTACHMENT_ACCEPT.join(',')),
  FileValidators.maxSize(MAX_SKILL_ATTACHMENT_SIZE, {
    title: '附件大小超出限制',
    message: '每个附件大小不能超过 10M',
  }),
]);

export function toSkillAttachmentFileList(
  attachments: SkillAttachmentDto[],
): UploadFile<UploadFileOutputDto>[] {
  return attachments.map((attachment) => ({
    uid: attachment.id,
    name: attachment.filename,
    status: 'done',
    url: attachment.file_url,
    response: attachment,
  }));
}

export function toSkillAttachmentIds(
  file_list: UploadFile<UploadFileOutputDto>[],
): UUIDString[] {
  return file_list.flatMap((file) => {
    const file_id = file.status === 'done' ? file.response?.id : undefined;

    return file_id ? [file_id] : [];
  });
}

const attachmentStatusTest: TestFunction<
  UploadFile<UploadFileOutputDto>[] | undefined
> = (values, ctx) => {
  if (!values?.length) {
    return true;
  }

  if (values.some((item) => item.status === 'uploading')) {
    return ctx.createError({ message: '附件正在上传中，请稍等' });
  }

  if (values.some((item) => item.status !== 'done' || !item.response?.id)) {
    return ctx.createError({ message: '存在上传失败的附件，请移除后重试' });
  }

  return true;
};

export const skillAttachmentSchema = array()
  .of(mixed<UploadFile<UploadFileOutputDto>>().required())
  .max(
    MAX_SKILL_ATTACHMENT_COUNT,
    `附件最多上传 ${MAX_SKILL_ATTACHMENT_COUNT} 个`,
  )
  .test(attachmentStatusTest)
  .default([]);
